import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { DrawerForm } from "../Common/styles/drawer";
import { InfoOutlined } from "@mui/icons-material";
import { featuresMap } from "../../utils/features";
import { useAppSelector } from "../../Redux/hooks";

let schema = yup.object().shape({
  name: yup.string().required("Form Name is Required"),
  duration: yup.number().optional(),
  location: yup.string().required("Destination is Required"),
  suffix: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_-]*$/,
      "Allowed characters: Alphanumeric, hyphen(-) and underscore(_)"
    )
    .required("Suffix is Required"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  data: any;
  studyId: any;
  refreshPage: () => void;
  formType: "platform" | "study";
};

const CopyFormModal = ({
  showModal,
  closeModal,
  data,
  studyId,
  refreshPage,
  formType,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [type, setType] = useState("screening");
  const [phases, setPhases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const features = useAppSelector((state) => state.study.features);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.name || "",
        description: values?.description || "",
        suffix: values?.suffix || "",
        location: values?.location || "",
      };
      const prefix =
        formType === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      const phase = formType === "platform" ? "registry-phase" : "study-phase";
      const res = await http.post(
        `${prefix}${phase}/${data?.formId}/clone`,
        body
      );
      toastMessage("success", res.data.message);
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  useEffect(() => {
    const fetchData = async (type: string) => {
      try {
        setLoading(true);
        const prefix =
          formType === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
        const phase =
          formType === "platform" ? "registry-phase" : "study-phase";
        const res: any = await http.get(
          `${prefix}${phase}/minimal?category=${type}`
        );
        const visits = res.data?.data
          ?.map((item: any) => ({
            id: item?.id,
            name: item?.name || "",
            position: item?.position || 1,
          }))
          .sort((a: any, b: any) => a.position - b.position);
        setPhases(visits);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData(type);
  }, [type, setLoading, setPhases, studyId, formType]);

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={"Copy Form " + data?.name}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            name: "",
            description: "",
            suffix: "",
            location: "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box sx={{ display: "grid" }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="form-name">
                    Copy to
                  </FormLabel>
                  <RadioGroup
                    row
                    value={type}
                    onChange={(event) => {
                      setType(event.target.value);
                      setFieldValue("location", "");
                    }}
                  >
                    {(features?.[featuresMap.visit] ||
                      formType === "platform") && (
                      <FormControlLabel
                        key={"screening"}
                        control={<Radio />}
                        label={"Screening"}
                        value={"screening"}
                      />
                    )}
                    {features?.[featuresMap.visit] && formType === "study" && (
                      <FormControlLabel
                        key={"visit"}
                        control={<Radio />}
                        label={"Visit"}
                        value={"visit"}
                      />
                    )}
                    {features?.[featuresMap.repeatedData] &&
                      formType === "study" && (
                        <FormControlLabel
                          key={"repeated_data"}
                          control={<Radio />}
                          label={"Repeating Data"}
                          value={"repeated_data"}
                        />
                      )}
                    {features?.[featuresMap.survey] && formType === "study" && (
                      <FormControlLabel
                        key={"survey"}
                        control={<Radio />}
                        label={"Survey"}
                        value={"survey"}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <FormLabel sx={LabelStyle} htmlFor="form-name">
                      Destination <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    {loading && <CircularProgress size={20} />}
                  </Box>
                  <Select
                    id="location"
                    value={values.location}
                    onChange={(e) => {
                      setFieldValue("location", e.target.value);
                    }}
                  >
                    {phases?.length ? (
                      phases.map((item: any) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem key={"None"} value={""} disabled>
                        No Data available
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText
                    error={touched?.location && errors?.location ? true : false}
                  >
                    {touched?.location && errors?.location
                      ? (errors?.location as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="form-name">
                    New Form Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Name"
                    id="form-name"
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="description">
                    Form Description
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    placeholder="Form description"
                    id="description"
                    {...getFieldProps("description")}
                    error={
                      touched?.description && errors?.description ? true : false
                    }
                    helperText={
                      touched?.description && errors?.description
                        ? (errors?.description as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel
                    sx={{
                      ...LabelStyle,
                      display: "flex",
                      alignItems: "center",
                    }}
                    htmlFor="form-suffix"
                  >
                    Suffix
                    <Tooltip
                      title={
                        "The suffix text is appended to the variable name to ensure uniqueness"
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ cursor: "default" }}>
                        <InfoOutlined />
                      </IconButton>
                    </Tooltip>
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Suffix"
                    id="form-suffix"
                    {...getFieldProps("suffix")}
                    error={touched?.suffix && errors?.suffix ? true : false}
                    helperText={
                      touched?.suffix && errors?.suffix
                        ? (errors?.suffix as string)
                        : " "
                    }
                  />
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Typography color="gray" mb={1}>
                  Note: Some features such as Randomize, Exclude Participant,
                  Repeated Measure, Calculated Fields, and Dependencies are not
                  copied to the Destination.
                </Typography>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {data?.id ? "Save" : "Add Visit"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default CopyFormModal;
